/**
 * トークン更新（refresh）の共通クライアント。
 *
 * axios インターセプター（main.tsx）と AuthProvider の両方から呼ばれるため、
 * React の外側（モジュールスコープ）に置く。in-flight Promise をメモ化して
 * 同時並行の refresh リクエストを 1 本に dedup する。
 */
import { saveTokenExpiry } from "@/lib/session";
import { FUNCTIONS_BASE_URL } from "@/lib/config";

// 実行中の refresh リクエスト。複数経路からの同時呼び出しを 1 本に集約する。
let refreshInFlight: Promise<boolean> | null = null;

/**
 * refresh を 1 回だけ実行する（dedup 付き）。
 * 成功時は idToken / refreshToken / expiresAt を更新して true を返す。
 * `lastActive` は意図的に更新しない（ユーザー操作のみで延命させ、
 * バックグラウンド refresh により無操作タイムアウトが無効化されるのを防ぐため）。
 */
export const refreshIdTokenRequest = (): Promise<boolean> => {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async (): Promise<boolean> => {
    try {
      // refreshToken の取得も try 内に置く。早期 return でも finally が確実に走り、
      // refreshInFlight が解放されないと以後 refresh が二度と再試行できなくなるため。
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) return false;
      const response = await fetch(`${FUNCTIONS_BASE_URL}/refreshIdToken`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        // expiresIn が不正値（NaN / 0 / 負数 / Infinity / undefined）の場合、
        // saveTokenExpiry が expiresAt を削除してスケジューラが動かなくなり、
        // 以後 isTokenExpired が常に true になるなどの不整合を招く。
        // 念のためここで検証し、不正なら refresh 失敗として扱う。
        const expiresInSeconds = Number(data.expiresIn);
        if (!Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) {
          console.warn(
            "Token refresh returned invalid expiresIn:",
            data.expiresIn
          );
          return false;
        }
        // リクエスト in-flight 中に logout() / clearAuthStorage() が走った場合、
        // 古い refresh 結果で localStorage を「復活」させない。
        // 開始時点の refreshToken が localStorage に残っていなければ stale 扱いで破棄する。
        if (localStorage.getItem("refreshToken") !== refreshToken) {
          return false;
        }
        localStorage.setItem("idToken", data.idToken);
        localStorage.setItem("refreshToken", data.refreshToken);
        // 注意: `lastActive` はユーザー操作（main.tsx の activity listeners）でのみ更新する。
        // バックグラウンド refresh で更新すると、毎周期 lastActive が延命されて
        // 120 分無操作タイムアウトが実質無効化されてしまうため、ここでは敢えて触らない。
        saveTokenExpiry(data.expiresIn);
        return true;
      }
      console.warn("Token refresh failed:", data.error || "Unknown error");
      return false;
    } catch (error) {
      console.error("Error during token refresh:", error);
      return false;
    } finally {
      // 早期 return / 例外含む全パスで in-flight を解放
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
};

/**
 * refresh を最大 `maxRetries` 回まで再試行する（指数バックオフ付き）。
 * `maxRetries = 1` の場合、初回 + 1 リトライ＝最大 2 回試行する。
 */
export const refreshWithRetry = async (
  maxRetries = 1,
  baseDelayMs = 1000
): Promise<boolean> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const ok = await refreshIdTokenRequest();
    if (ok) return true;
    if (attempt < maxRetries) {
      const delay = baseDelayMs * 2 ** attempt;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  return false;
};
